import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import PropTypes from 'prop-types';
import React, { createRef, useCallback, useRef, useState } from 'react';

import Chevron from 'components/Chevron';
import ResponsiveImage from 'components/ResponsiveImage';
import RichText from 'components/RichText';
import VideoPlayer from 'components/VideoPlayer';
import { IMAGE_WIDTHS } from 'constants/images';
import { breakpoints, cn } from 'utils/Tailwind';

import '@splidejs/react-splide/css';
import 'styles/tailwind.css';

const CAROUSEL_OPTIONS = {
  type: 'slide',
  pagination: false,
  perPage: 1,
  gap: '0.5rem',
  padding: {
    left: '24px',
    right: '24px',
  },
  focus: 'center',
  mediaQuery: 'min',
  dragMinThreshold: 40,
  breakpoints: {
    [breakpoints.md]: {
      padding: {
        left: '40px',
        right: '40px',
      },
    },
  },
};

const useTabNavigation = items => {
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const carouselRef = useRef(null);
  const navRefs = useRef(items.map(() => createRef()));
  const navContainerRef = useRef(null);

  const handleCategoryChange = useCallback(index => {
    const containerElement = navContainerRef.current;
    const navElement = navRefs.current?.[index]?.current;
    const centerViewPoint = containerElement?.clientWidth ? containerElement.clientWidth / 2 : 0;
    const elementPosition = navElement?.offsetLeft ?? 0;
    const elementWidth = navElement?.clientWidth ?? 0;

    if (containerElement && navElement) {
      containerElement.scroll({
        left: elementPosition - centerViewPoint + elementWidth / 2,
        behavior: 'smooth',
      });

      setActiveTabIndex(index);
      carouselRef?.current?.splide?.go?.(index);
    }
  }, []);

  return {
    activeTabIndex,
    carouselRef,
    navRefs,
    navContainerRef,
    handleCategoryChange,
  };
};

const PageSectionPropositionsCarousel = ({ items, dataTestId }) => {
  const { activeTabIndex, carouselRef, navRefs, navContainerRef, handleCategoryChange } =
    useTabNavigation(items);

  return (
    <section
      className="PropositionCollection relative my-6 flex flex-col md:my-10"
      {...(dataTestId && { 'data-testid': dataTestId })}
    >
      <TabNavigation
        activeTabIndex={activeTabIndex}
        handleCategoryChange={handleCategoryChange}
        items={items}
        navContainerRef={navContainerRef}
        navRefs={navRefs}
      />

      <div className="w-full md:flex md:flex-col md:justify-center">
        <Splide
          data-testid="splide"
          hasTrack={false}
          onMove={({ index }) => {
            handleCategoryChange(index);
          }}
          options={CAROUSEL_OPTIONS}
          ref={carouselRef}
        >
          <CarouselArrows activeTabIndex={activeTabIndex} items={items} />

          <SplideTrack>
            {items.map(({ title, items }) => (
              <SplideSlide
                key={title}
                className="flex flex-col gap-y-2 md:grid md:grid-cols-2 md:gap-2"
              >
                <PropositionSlide items={items} title={title} />
              </SplideSlide>
            ))}
          </SplideTrack>
        </Splide>
      </div>
    </section>
  );
};

const TabNavigation = ({
  activeTabIndex,
  items,
  navContainerRef,
  navRefs,
  handleCategoryChange,
}) => {
  return (
    <nav
      aria-label="Proposition Collection Navigation"
      className="no-scrollbar mb-4 flex overflow-x-scroll scroll-smooth pl-6 md:mb-8 md:pl-10"
      ref={navContainerRef}
    >
      {items.map(({ title }, index) => (
        <button
          className={cn(
            'transition-border pb-.5 mr-8 whitespace-nowrap border-b-2 border-transparent text-sm font-bold duration-200 ease-in-out md:mr-12 md:text-base md:hover:border-black md:hover:text-black',
            {
              'border-black text-black': activeTabIndex === index,
              'text-black/80': activeTabIndex !== index,
            }
          )}
          onClick={() => handleCategoryChange(index)}
          key={title}
          ref={navRefs.current[index]}
        >
          {title}
        </button>
      ))}
    </nav>
  );
};

const CarouselArrows = ({ activeTabIndex, items }) => {
  return (
    <div className="splide__arrows hidden md:block">
      <button
        className={cn(`splide__arrow splide__arrow--prev !left-4`, {
          '!hidden': activeTabIndex === 0,
        })}
      >
        <Chevron direction="left" size={9} />
      </button>
      <button
        className={cn(`splide__arrow splide__arrow--next !right-4`, {
          '!hidden': activeTabIndex === items.length - 1,
        })}
      >
        <Chevron direction="right" size={9} />
      </button>
    </div>
  );
};

const PropositionSlide = ({ items }) => {
  return (
    <>
      {items.map(({ asset, isInverted, title, body, overlayPosition }) => (
        <div className="group/card relative flex flex-col overflow-hidden" key={title}>
          {asset.file.contentType.includes('video') ? (
            <VideoPlayer
              className="absolute inset-0"
              url={asset.file.url}
              playing={true}
              aspectRatio="1:1"
              muted
              loop
              playsinline
            />
          ) : (
            <ResponsiveImage
              alt={asset.description}
              className="w-full transition-transform duration-300 ease-in-out md:group-hover/card:scale-105"
              src={asset.file.url}
              width={IMAGE_WIDTHS.W_1000}
              height={IMAGE_WIDTHS.W_1000}
              lazy
            />
          )}

          {title && body && (
            <div
              className={cn(
                'absolute inset-0 flex flex-col items-start bg-black/20 p-10',
                [isInverted && 'text-white'],
                [overlayPosition === 'Bottom' && 'justify-end'],
                [overlayPosition === 'Top' && 'justify-start']
              )}
            >
              <p className="mb-2 text-lg/6 font-bold sm:text-2xl md:text-1xl/7 lg:text-2xl">
                {title}
              </p>
              {body && <RichText content={body} className="max-w-[40ch] [&_p]:!mb-0" />}
            </div>
          )}
        </div>
      ))}
    </>
  );
};

PageSectionPropositionsCarousel.propTypes = {
  dataTestId: PropTypes.string,
  internalName: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string,
          body: PropTypes.shape({}),
          url: PropTypes.string,
          isInverted: PropTypes.bool,
          asset: PropTypes.shape({
            description: PropTypes.string,
            file: PropTypes.shape({
              url: PropTypes.string,
              contentType: PropTypes.string,
            }),
          }),
        })
      ),
    })
  ),
};

PageSectionPropositionsCarousel.defaultProps = {
  internalName: '',
  items: [],
};

TabNavigation.propTypes = {
  activeTabIndex: PropTypes.number,
  items: PropTypes.array,
  navContainerRef: PropTypes.any,
  navRefs: PropTypes.shape({
    current: PropTypes.any,
  }),
  handleCategoryChange: PropTypes.func,
};

TabNavigation.defaultProps = {
  activeTabIndex: 0,
  items: [],
  navContainerRef: null,
  navRefs: { current: [] },
};

CarouselArrows.propTypes = {
  activeTabIndex: PropTypes.number,
  items: PropTypes.array,
};

CarouselArrows.defaultProps = {
  activeTabIndex: 0,
  items: [],
};

PropositionSlide.propTypes = {
  items: PropTypes.array,
};

PropositionSlide.defaultProps = {
  items: [],
};

export default PageSectionPropositionsCarousel;
